import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from 'react-query';
import { DefaultGetResponse } from '~/App/Api/rest';
import { CreditCard, PaymentIntent } from '~/types/models';
import axios, { AxiosError } from 'axios';
import { billingApi } from '../utils';
import { BillingDetails } from '~/types/stripe';
import { SetupIntentResult, StripeCardElement } from '@stripe/stripe-js';
import { Stripe } from '@stripe/stripe-js';

const creditCardApi = `${billingApi}/credit-cards`;

export const queryKeys = {
  detail: ['credit-card', 'detail'] as const,
};

// Get Credit Card interface, function, and hook
export interface GetCreditCardResponse {
  data: CreditCard[];
}

export async function getCreditCard() {
  const res = await axios.get<GetCreditCardResponse>(creditCardApi);
  return res.data.data[0];
}

export interface UseCreditCardParam {
  options?: UseQueryOptions<DefaultGetResponse<CreditCard>, AxiosError>;
}

export const useCreditCard = ({ options }: UseCreditCardParam) =>
  useQuery<DefaultGetResponse<CreditCard>, AxiosError>(
    queryKeys.detail,
    getCreditCard,
    options
  );

// Generates a payment intentfrom the backend
export async function getPaymentIntent() {
  const res = await axios.get<PaymentIntent>(`${billingApi}/payment-intent`);
  return res.data;
}

// Update Credit Card interface, function, and hook
export interface UpdateCreditCardArgs {
  billingDetails: BillingDetails;
  cardElement: StripeCardElement;
  stripe: Stripe | null;
}

// Updates the credit card details to Stripe
export async function updateCreditCardToStripe({
  billingDetails,
  cardElement,
  stripe,
}: UpdateCreditCardArgs) {
  const secret = (await getPaymentIntent()).client_secret;

  const payload = {
    payment_method: {
      card: cardElement,
      billing_details: billingDetails,
    },
  };
  return (stripe as Stripe).confirmCardSetup(secret, payload);
}

export interface CreditCardWithIntentId extends CreditCard {
  intentId?: string;
}

export async function updateCreditCard(intentId?: string) {
  const res = await axios.post<CreditCardWithIntentId>(creditCardApi, {
    intent_id: intentId,
  });
  return { ...res.data, intentId };
}

export type UpdateCreditCardReturnType =
  | CreditCardWithIntentId
  | SetupIntentResult;

export interface UpdateCreditCardParams {
  options?: UseMutationOptions<
    UpdateCreditCardReturnType,
    AxiosError,
    UpdateCreditCardArgs
  >;
}

export const useUpdateCreditCard = ({ options }: UpdateCreditCardParams) =>
  useMutation<UpdateCreditCardReturnType, AxiosError, UpdateCreditCardArgs>(
    async (args: UpdateCreditCardArgs) => {
      const result = await updateCreditCardToStripe(args);
      if (!result.error) {
        return updateCreditCard(result?.setupIntent?.id);
      } else {
        return result;
      }
    },
    options
  );
